<template>
  <div>
    <woot-modal-header
      :header-title="
        isEditMode
          ? $t('AI.KNOWLEDGE.EDIT.TITLE_FILE')
          : $t('AI.KNOWLEDGE.ADD.TITLE_FILE')
      "
    >
      <p>
        {{
          isEditMode
            ? $t('AI.KNOWLEDGE.EDIT.SUB_TITLE')
            : $t('AI.KNOWLEDGE.ADD.SUB_TITLE')
        }}
      </p>
    </woot-modal-header>
    <div class="modal-content">
      <InputField
        v-model="knowledge.name"
        :label="$t('AI.KNOWLEDGE.ADD.NAME.LABEL')"
        :placeholder="$t('AI.KNOWLEDGE.ADD.NAME.PLACEHOLDER')"
        :info-list="[]"
      />

      <InputFileUploadDragDrop
        v-if="!isEditMode || isFileDeleted"
        :multiple="false"
        @change="onFileChange"
      />

      <div v-if="$v.files.$error" class="error-message my_14">
        <span class="message">{{ $t('AI.KNOWLEDGE.ADD.FILE.ERROR') }}</span>
      </div>

      <!-- Existing file display in edit mode -->
      <div v-if="isEditMode && !isFileDeleted" class="my_14">
        <div class="tool_file py_8">
          <div>
            <fluent-icon icon="document" />
          </div>

          <div>
            <p>
              <strong>{{ initialItem.file_name }}</strong>
            </p>
          </div>

          <div @click="onRemoveExistingFile">
            <fluent-icon icon="delete" />
          </div>
        </div>
      </div>

      <!-- Newly uploaded files display -->
      <div
        v-if="(!isEditMode || isFileDeleted) && files.length > 0"
        class="my_14"
      >
        <div
          v-for="(file, index) in files"
          :key="file.name + index"
          class="tool_file py_8"
        >
          <div>
            <fluent-icon icon="document" />
          </div>

          <div>
            <p>
              <strong>{{ file.name }}</strong>
            </p>
            <p>{{ formatSize(file.size) }}</p>
          </div>

          <div @click="removeFile(index)">
            <fluent-icon icon="delete" />
          </div>
        </div>
      </div>
      <div class="mt_14">
        <woot-button
          :is-disabled="uiFlags.isKnowledgeCreating"
          :is-loading="
            uiFlags.isKnowledgeCreating || uiFlags.isKnowledgeUpdating
          "
          @click="onSubmit"
        >
          {{
            isEditMode
              ? $t('AI.KNOWLEDGE.EDIT.BTNS.SUBMIT')
              : $t('AI.KNOWLEDGE.ADD.BTNS.SUBMIT')
          }}
        </woot-button>
      </div>
    </div>
  </div>
</template>

<script>
import alertMixin from 'shared/mixins/alertMixin';
import InputFileUploadDragDrop from 'components/ui/inputs/InputFileUploadDragDrop.vue';
import InputField from 'components/ui/inputs/InputField.vue';
import { mapGetters } from 'vuex';
import { maxLength } from 'vuelidate/lib/validators';

export default {
  components: {
    InputFileUploadDragDrop,
    InputField,
  },
  mixins: [alertMixin],
  props: {
    initialItem: {
      type: Object,
      default: () => ({}),
    },
  },
  data() {
    return {
      knowledge: {
        name: '',
      },
      files: [],
      isFileDeleted: false,
    };
  },
  computed: {
    ...mapGetters({
      uiFlags: 'tools/getUIFlags',
    }),
    isEditMode() {
      return this.initialItem && this.initialItem.id;
    },
  },
  validations() {
    return {
      files: {
        minLength: value => {
          if (this.isEditMode && !this.isFileDeleted) return true;
          return value.length >= 1;
        },
        maxLength: maxLength(1),
      },
    };
  },
  watch: {
    initialItem: {
      immediate: true,
      handler(newVal) {
        if (newVal && newVal.name) {
          this.knowledge.name = newVal.name;
        }
        // Reset file deleted state when opening edit mode
        if (newVal && newVal.id) {
          this.isFileDeleted = false;
        } else {
          this.isFileDeleted = false;
          this.files = [];
        }
      },
    },
  },
  methods: {
    async onSubmit() {
      try {
        this.$v.$touch();

        if (this.$v.$invalid) {
          this.showAlert(this.$t('AI.KNOWLEDGE.ADD.FILE.ERROR'));
          return;
        }

        // File is only required when adding OR when in edit mode and file was deleted
        if (!this.isEditMode || this.isFileDeleted) {
          if (this.files.length !== 1) {
            this.showAlert(this.$t('AI.KNOWLEDGE.ADD.FILE.ERROR'));
            return;
          }
        }

        const payload = new FormData();
        payload.append('kind', 'document');

        if (this.knowledge.name) {
          payload.append('name', this.knowledge.name);
        }

        // Only append file if a new file was uploaded
        this.files.forEach(file => {
          payload.append('file', file);
        });

        if (this.isEditMode) {
          await this.$store.dispatch('tools/updateKnowledgeById', {
            knowledge_id: this.initialItem.id,
            payload,
          });
        } else {
          await this.$store.dispatch('tools/createKnowledge', payload);
        }

        this.$emit('onSubmit');
        this.showAlert(this.$t('AI.KNOWLEDGE.ADD.ALERTS.ADD_SUCCESS'));
        this.$emit('onClose');
      } catch (error) {
        console.error(error);
        this.showErrAlert(error, this.$t('CAMPAIGN.NETWORK_ERR.FETCH_ERR'));
      }
    },
    onFileChange(files) {
      if (this.files.length === 0) {
        this.files.push(...files.slice(0, 1));
      }
      this.$v.files.$reset();
    },
    removeFile(index) {
      this.files.splice(index, 1);
      this.$v.files.$touch();
    },
    onRemoveExistingFile() {
      this.isFileDeleted = true;
      // this.$v.files.$touch();
    },
    formatSize(bytes) {
      if (bytes < 1024) return `${bytes} B`;
      if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
      return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
    },
  },
};
</script>

<style scoped lang="scss">
.tool_file {
  display: grid;
  grid-template-columns: 25px 1fr 25px;
  gap: 10px;
}

.error-message {
  display: flex;
  align-items: center;
  margin-top: var(--space-small);
  color: var(--r-400);
  font-size: var(--font-size-mini);

  .message {
    margin: 0;
  }
}
</style>
